import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import MaintenancePrinterSummaryCard from '../../components/printer-maintenances/MaintenancePrinterSummaryCard';
import CustomPagination from '../../components/Pagination';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatDate, formatUuid } from '../../helpers/formatters';
import { sortPrintersByName } from '../../helpers/printers';
import {
    buildMaintenanceSummaryCards,
    buildPrinterMaintenanceListQuery,
    getMaintenanceTypeBadgeProps,
    MAINTENANCE_TYPES,
    minutesToDuration,
} from '../../helpers/printerMaintenances';

function ListPrinterMaintenancesPage() {
    const [searchParams] = useSearchParams();
    const initialPrinterUuid = searchParams.get('printer_uuid') || '';
    const [maintenances, setMaintenances] = useState([]);
    const [summaryMaintenances, setSummaryMaintenances] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [printersById, setPrintersById] = useState({});
    const [draftPrinter, setDraftPrinter] = useState(initialPrinterUuid);
    const [draftType, setDraftType] = useState('');
    const [queryFilters, setQueryFilters] = useState({
        printer_uuid: initialPrinterUuid,
        type: '',
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasActiveFilters = Boolean(queryFilters.printer_uuid || queryFilters.type);

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                const list = sortPrintersByName(response.data.printers || []);
                setPrinters(list);
                const byId = {};
                list.forEach((printer) => {
                    byId[printer.id] = printer.name;
                });
                setPrintersById(byId);
            })
            .catch(() => {});
    }, []);

    const fetchMaintenances = useCallback(() => {
        setIsLoading(true);
        const listQuery = buildPrinterMaintenanceListQuery({
            page: currentPage,
            limit: itemsPerPage,
            printerUuid: queryFilters.printer_uuid,
            type: queryFilters.type,
        });
        const summaryQuery = buildPrinterMaintenanceListQuery({
            page: 1,
            limit: 500,
            printerUuid: queryFilters.printer_uuid,
            type: queryFilters.type,
        });

        Promise.all([
            api.get(`printer-maintenances?${listQuery}`),
            api.get(`printer-maintenances?${summaryQuery}`),
        ])
            .then(([listResponse, summaryResponse]) => {
                const data = listResponse.data.data || {};
                setMaintenances(data.maintenances || []);
                setTotalPages(data.total_pages || 1);
                setSummaryMaintenances(summaryResponse.data.data?.maintenances || []);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, queryFilters.printer_uuid, queryFilters.type]);

    const summaryCards = useMemo(
        () => buildMaintenanceSummaryCards(printers, summaryMaintenances)
            .filter((card) => card.downtime_minutes > 0),
        [printers, summaryMaintenances],
    );

    const maxSummaryDowntime = useMemo(
        () => summaryCards.reduce((max, card) => Math.max(max, card.downtime_minutes), 0),
        [summaryCards],
    );

    const handleSummaryPrinterSelect = (printerUuid) => {
        setDraftPrinter(printerUuid);
        setQueryFilters({
            printer_uuid: printerUuid,
            type: queryFilters.type,
        });
        triggerReload();
    };

    useListFetch(fetchMaintenances, [
        currentPage,
        itemsPerPage,
        listVersion,
        queryFilters.printer_uuid,
        queryFilters.type,
        fetchMaintenances,
    ]);

    const triggerReload = () => {
        setCurrentPage(1);
        setListVersion((version) => version + 1);
    };

    const handleApplyFilters = () => {
        setQueryFilters({
            printer_uuid: draftPrinter,
            type: draftType,
        });
        triggerReload();
    };

    const handleClearFilters = () => {
        setDraftPrinter('');
        setDraftType('');
        setQueryFilters({ printer_uuid: '', type: '' });
        triggerReload();
    };

    const handleDelete = (maintenanceId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta manutenção será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? 'Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                api.delete(`printer-maintenances/${maintenanceId}`)
                    .then(() => {
                        setMaintenances((prev) => prev.filter((maintenance) => maintenance.id !== maintenanceId));
                        Swal.fire('Removido!', 'A manutenção foi removida com sucesso.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover a manutenção.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Manutenções</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom>
                    Manutenções
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/admin/register-printer-maintenance')}
                    startIcon={<Add />}
                    fullWidth={isMobile}
                >
                    Manutenção
                </Button>
            </Box>

            {printers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        Paradas por impressora
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Ordenado pelo tempo total de interrupção (análise + correção), não pela quantidade de manutenções.
                    </Typography>
                    {summaryCards.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhuma manutenção encontrada com os filtros atuais.
                        </Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {summaryCards.map((printerCard) => {
                                const downtimePercent = maxSummaryDowntime > 0
                                    ? (printerCard.downtime_minutes / maxSummaryDowntime) * 100
                                    : 0;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={printerCard.printer_uuid}>
                                        <MaintenancePrinterSummaryCard
                                            printer={printerCard}
                                            downtimePercent={downtimePercent}
                                            onSelect={handleSummaryPrinterSelect}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Filtros
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-printer-label">Impressora</InputLabel>
                            <Select
                                labelId="filter-printer-label"
                                label="Impressora"
                                value={draftPrinter}
                                onChange={(event) => setDraftPrinter(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {printers.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-type-label">Tipo</InputLabel>
                            <Select
                                labelId="filter-type-label"
                                label="Tipo"
                                value={draftType}
                                onChange={(event) => setDraftType(event.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {MAINTENANCE_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        Filtrar
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tempo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contagem de horas</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Peças</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(maintenances) && maintenances.length > 0 && maintenances.map((maintenance) => (
                            <TableRow key={maintenance.id}>
                                <TableCell>{formatDate(maintenance.occurred_at)}</TableCell>
                                <TableCell>{printersById[maintenance.printer_uuid] || formatUuid(maintenance.printer_uuid)}</TableCell>
                                <TableCell>
                                    <Chip {...getMaintenanceTypeBadgeProps(maintenance.type)} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{maintenance.problem_description}</Typography>
                                    {maintenance.performed_actions && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Feito: {maintenance.performed_actions}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        Análise: {minutesToDuration(maintenance.analysis_duration_minutes)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Correção: {minutesToDuration(maintenance.correction_duration_minutes)}
                                    </Typography>
                                </TableCell>
                                <TableCell>{maintenance.printer_hours_count}h</TableCell>
                                <TableCell>
                                    {Array.isArray(maintenance.replacement_parts) && maintenance.replacement_parts.length > 0
                                        ? maintenance.replacement_parts.map((part) => (typeof part === 'string' ? part : part?.name)).filter(Boolean).join(', ')
                                        : 'Nenhuma'}
                                </TableCell>
                                <TableCell>
                                    <IconButton color="primary" onClick={() => navigate(`/admin/edit-printer-maintenance/${maintenance.id}`)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => handleDelete(maintenance.id)} aria-label="remover manutenção">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 3 }}>
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        setItemsPerPage(event.target.value);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListPrinterMaintenancesPage;
